import {
  GeckoAssetModelJSON,
  GeckoAssetModelType,
  GeckoUnit,
  RolodexEntryPutJson,
  RolodexEntryTagsType,
} from 'types'
import { getFormattedInstallationDateString } from 'utils/installationDateHelpers'

/**
 * Private function supporting the generateAssetModel function. This function builds the JSON entry that can be provided
 * directly to Rolodex to create or update asset models.
 * @param name                  The name of the asset model
 * @param unit                  The GeckoUnit that an asset model will be created and linked to
 * @param assetModelType        The GeckoAssetModelType that represents the real world unit
 * @param installationDate      The date the asset was manufactured
 * @param effectiveDate         The date when this asset model became effective (to be used as version number)
 * @param compassOrientation    The compass degrees direction in which "zero" points
 */
function generateAssetModelEntry(
  name: string,
  unit: GeckoUnit,
  assetModelType: GeckoAssetModelType,
  installationDate: string | Date | undefined,
  effectiveDate: string | Date | undefined,
  compassOrientation: number | undefined,
  plantNorthCompassOrientation: number | undefined
): RolodexEntryPutJson {
  // Check that we have the necessary information from the GeckoUnit
  const unitID = unit.id
  const unitTagSite = unit.tags.site
  const unitTagOrganization = unit.tags.organization

  // Uniformize the effective date to ensure it only consist of the date, not timestamp
  let effective_date
  if (typeof effectiveDate === 'string') {
    effective_date = new Date(effectiveDate).toISOString().split('T')[0]
  } else if (effectiveDate instanceof Date) {
    effective_date = effectiveDate.toISOString().split('T')[0]
  } else {
    effective_date = new Date().toISOString().split('T')[0]
  }

  const installation_date = !installationDate
    ? undefined
    : getFormattedInstallationDateString(installationDate)

  // Version number is just the effective date, with the hyphens removed
  const versionNumber = effective_date.replaceAll('-', '')
  const assetModelID = `${unitID}-asset-model-${versionNumber}`

  const assetModelData: GeckoAssetModelJSON = {
    name: name,
    type: assetModelType,
    components: {},
    installation_date: installation_date,
    effective_date: effective_date,
    compass_orientation: compassOrientation,
    plant_north_compass_orientation: plantNorthCompassOrientation,
  }

  return {
    id: assetModelID,
    type: 'asset-model',
    data: assetModelData,
    tags: {
      site: unitTagSite,
      organization: unitTagOrganization,
      unit: unit.id,
    } as RolodexEntryTagsType,
    index: {},
  } as RolodexEntryPutJson
}

/**
 * Function to create a new Asset Model JSON based on a unit and (optionally) a component.
 * Optionally, you can provide an existing Asset Model and the provided Component will be appended and returned.
 * @param unit                  The GeckoUnit that an asset model will be created and linked to
 * @param unitType              The GeckoAssetModelType that represents the real world unit
 * @param installationDate      The date the asset was manufactured
 * @param effectiveDate         The date when this asset model became effective (to be used as version number)
 * @param compassOrientation    The compass degrees direction in which "zero" points
 */
export const generateAssetModel = (
  unit: GeckoUnit,
  unitType: GeckoAssetModelType,
  installationDate?: string | Date | undefined,
  effectiveDate?: string | Date | undefined,
  compassOrientation?: number | undefined,
  plantNorthCompassOrientation?: number | undefined
) => {
  const assetModelName = `${unit.id}-asset-model`

  if (!unitType) {
    throw new Error('Unable to generate asset model. unitType was not defined!')
  }

  return generateAssetModelEntry(
    assetModelName,
    unit,
    unitType as GeckoAssetModelType,
    installationDate,
    effectiveDate,
    compassOrientation,
    plantNorthCompassOrientation
  )
}
